'use client';

/**
 * 交互对齐 black-friday/ClientPage + ProductNavigation：
 * - 滚动中暂停自动高亮（isScrolling + checkScrollEnd）
 * - 以「区块中心距视口中心最近」更新选中项
 * - 点击平滑滚动，offset = --global-header-height + 导航栏高度（与 ClientPage.executeScroll 一致），再 +extraScrollDownPx 多滚一点
 * - WAP 横向 overflow-x-auto + scrollbar-hidden，选中项 scrollTo 居中
 */

import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { EPlatform } from '@/utils/enums/platform-enum';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';

export interface MeasurePDNavItem {
  id: string;
  label: string;
}

interface MeasurePDNavProps {
  ariaLabel: string;
  items: MeasurePDNavItem[];
  /** 非吸顶区域的外边距（如与首段标题间距） */
  className?: string;
}

function NavLabel({ active, children }: { active: boolean; children: ReactNode }) {
  return (
    <span
      className={[
        'inline-block whitespace-nowrap border-b-[3px] md:border-b-[2px] pt-[18px] pb-[9px] md:pt-[16px] md:pb-[12px] 2xl:pt-[14px] 2xl:pb-[14px]',
        active
          ? 'relative z-[1] -mb-px border-[#282828] font-bb text-[14px] md:text-[21px] 2xl:text-[28px]'
          : 'border-transparent font-normal text-[#666666] md:text-[#282828] text-[14px] md:text-[17px] 2xl:text-[20px]',
      ].join(' ')}
    >
      {children}
    </span>
  );
}

function getDefaultActiveId(items: MeasurePDNavItem[]) {
  /** 首屏为导语区，无对应锚点；默认高亮第一项 */
  return items[0]?.id ?? '';
}

export default function MeasurePDNav({ ariaLabel, items, className = '' }: MeasurePDNavProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isApp = [EPlatform.ANDROID, EPlatform.IOS].includes(deviceType);

  /** 与 ProductNavigation 一致：App 内顶栏常隐藏，用 top:0；Web 用 #header 实际高度（含顶通） */
  const [stickyTopPx, setStickyTopPx] = useState<number | null>(null);

  const syncStickyTopFromHeader = useCallback(() => {
    if (typeof window === 'undefined') return;
    if (isApp) {
      setStickyTopPx(0);
      return;
    }
    const el = document.getElementById('header');
    setStickyTopPx(el ? el.offsetHeight : 100);
  }, [isApp]);

  useLayoutEffect(() => {
    syncStickyTopFromHeader();
    const el = document.getElementById('header');
    const ro = new ResizeObserver(() => syncStickyTopFromHeader());
    if (el) ro.observe(el);
    window.addEventListener('resize', syncStickyTopFromHeader);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncStickyTopFromHeader);
    };
  }, [syncStickyTopFromHeader]);

  const [activeId, setActiveId] = useState(() => getDefaultActiveId(items));
  const isScrolling = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scrollEndCheckRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastScrollTop = useRef(0);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);

  const checkScrollEnd = useCallback(() => {
    const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

    if (Math.abs(currentScrollTop - lastScrollTop.current) < 1) {
      isScrolling.current = false;
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
        scrollEndCheckRef.current = null;
      }
    } else {
      lastScrollTop.current = currentScrollTop;
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
    }
  }, []);

  const scrollToCenter = useCallback((index: number) => {
    if (typeof window !== 'undefined' && window.innerWidth >= 768) return;

    const container = scrollContainerRef.current;
    const item = itemRefs.current[index];

    if (container && item) {
      const containerWidth = container.offsetWidth;
      const itemLeft = item.offsetLeft;
      const itemWidth = item.offsetWidth;
      const scrollLeft = itemLeft - (containerWidth - itemWidth) / 2;

      container.scrollTo({
        left: Math.max(0, scrollLeft),
        behavior: 'smooth',
      });
    }
  }, []);

  const handleScroll = useCallback(() => {
    if (isScrolling.current) {
      const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;
      lastScrollTop.current = currentScrollTop;

      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }
      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);
      return;
    }

    const windowHeight = window.innerHeight;
    let currentIndex = 0;
    let minDistance = Infinity;

    items.forEach((item, index) => {
      const el = document.getElementById(item.id);
      if (el) {
        const rect = el.getBoundingClientRect();
        const sectionCenter = rect.top + rect.height / 2;
        const viewportCenter = windowHeight / 2;
        const distance = Math.abs(sectionCenter - viewportCenter);

        if (distance < minDistance) {
          minDistance = distance;
          currentIndex = index;
        }
      }
    });

    const nextId = items[currentIndex]?.id;
    if (nextId) {
      setActiveId((prev) => (prev !== nextId ? nextId : prev));
    }
  }, [items, checkScrollEnd]);

  const executeScroll = useCallback(
    (index: number) => {
      const target = document.getElementById(items[index]?.id);
      if (!target) return;

      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (scrollEndCheckRef.current) {
        clearTimeout(scrollEndCheckRef.current);
      }

      isScrolling.current = true;
      lastScrollTop.current = window.pageYOffset || document.documentElement.scrollTop;

      const headerHeight = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--global-header-height') ||
          '100',
        10,
      );

      const navEl = document.querySelector('.measure-pd-navigation');
      const navHeight = navEl ? navEl.getBoundingClientRect().height : 52;
      let offset = headerHeight + navHeight;

      if (typeof window !== 'undefined' && window.innerWidth < 768) {
        const adaptiveOffset = (50 / 375) * window.innerWidth;
        offset -= adaptiveOffset;
      }

      /** 在原有定位基础上再多滚一点，盖住上一模块底边；单位 px（若需约 1cm 可改为 ~38） */
      const extraScrollDownPx = 10;
      const targetTop =
        target.getBoundingClientRect().top + window.scrollY - offset + extraScrollDownPx;

      window.scrollTo({
        top: Math.max(0, targetTop),
        behavior: 'smooth',
      });

      scrollEndCheckRef.current = setTimeout(checkScrollEnd, 150);

      scrollTimeoutRef.current = setTimeout(() => {
        isScrolling.current = false;
        if (scrollEndCheckRef.current) {
          clearTimeout(scrollEndCheckRef.current);
          scrollEndCheckRef.current = null;
        }
      }, 2000);
    },
    [items, checkScrollEnd],
  );

  const onNavClick = useCallback(
    (index: number) => {
      const id = items[index]?.id;
      if (id) setActiveId(id);
      executeScroll(index);
      scrollToCenter(index);
    },
    [items, executeScroll, scrollToCenter],
  );

  useEffect(() => {
    if (activeId) {
      const idx = items.findIndex((i) => i.id === activeId);
      if (idx >= 0) {
        scrollToCenter(idx);
      }
    }
  }, [activeId, items, scrollToCenter]);

  const hasHandledHash = useRef(false);

  useEffect(() => {
    if (hasHandledHash.current) return;
    hasHandledHash.current = true;
    const hash = typeof window !== 'undefined' ? window.location.hash.slice(1) : '';
    if (!hash) return;
    const idx = items.findIndex((i) => i.id === hash);
    if (idx < 0) return;
    setActiveId(items[idx].id);
    requestAnimationFrame(() => {
      executeScroll(idx);
      scrollToCenter(idx);
    });
  }, [items, executeScroll, scrollToCenter]);

  useEffect(() => {
    let ticking = false;

    const throttled = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener('scroll', throttled, { passive: true });
    window.addEventListener('resize', throttled, { passive: true });
    requestAnimationFrame(() => throttled());

    return () => {
      window.removeEventListener('scroll', throttled);
      window.removeEventListener('resize', throttled);
    };
  }, [handleScroll]);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (scrollEndCheckRef.current) clearTimeout(scrollEndCheckRef.current);
    };
  }, []);

  return (
    <nav
      aria-label={ariaLabel}
      className={[
        'measure-pd-navigation sticky z-[30] w-full border-b border-[#E5E5E5] bg-white',
        className,
        /* 与 ProductNavigation 一致；测量到 #header 高度后改用 style.top 覆盖（含顶通） */
        stickyTopPx === null && !isApp ? 'top-[11.733vw] lg:top-[100px]' : '',
        stickyTopPx === null && isApp ? 'top-0' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      style={stickyTopPx !== null ? { top: stickyTopPx } : undefined}
    >
      <div
        ref={scrollContainerRef}
        className="flex items-end gap-[16px] overflow-x-auto scrollbar-hidden md:gap-[40px] 2xl:gap-[64px] md:overflow-visible px-[14px] md:px-0"
      >
        {items.map((item, index) => (
          <div
            key={item.id}
            ref={(el) => {
              itemRefs.current[index] = el;
            }}
            className="flex-shrink-0"
          >
            <button type="button" className="cursor-pointer" onClick={() => onNavClick(index)}>
              <NavLabel active={activeId === item.id}>{item.label}</NavLabel>
            </button>
          </div>
        ))}
      </div>
    </nav>
  );
}
